import {
	Alert,
	Badge,
	Box,
	Button,
	Col,
	Component,
	Divider,
	Grid,
	Icons,
	Inline,
	Money,
	Stack,
	Table,
	Text,
} from "@sembark-travel/ui-base"
import {
	FieldArray,
	FormApi,
	GetFieldValue,
	SelectField,
	TextInputField,
	useFieldValue,
	useForm,
} from "@sembark-travel/ui-form"
import { useCallback, useEffect, useState } from "react"
import { FeatureFlag } from "../Auth"
import {
	InfoStack,
	SelectVisaServices,
	SelectVisaTypes,
	XHR,
	getEntryTypeDisplayName,
	getPurposeOfVisaDisplayName,
	visaProcessingTimeStr,
} from "../Visa"
import { TVisaServiceInQuote } from "./store"
import { formatMoneyByDecimal, multipleMoneyBy } from "@sembark-travel/money"
import { SelectWorldCountries } from "../World"
import { TTripDestination } from "../TripDestinations"
import { Markdown } from "@sembark-travel/ui-markdown"
import { useXHR, XHRInstance } from "@sembark-travel/xhr"
import { VisaTypeDisabled } from "../VisaBookings"
import {
	isVisaRateExpired,
	parseVisaRatePerPerson,
	getUniqueDocumentList,
} from "./utils"
import { InlineSelectTenantCurrencyInputField } from "../Currencies"

export function ServiceWiseInputFieldsArray({
	fieldName,
	tripDestinations,
	totalPax,
	isEditMode,
	showBookedPrices,
	startDate,
}: Readonly<{
	fieldName: string
	tripDestinations: Array<TTripDestination>
	totalPax: number
	isEditMode?: boolean
	showBookedPrices?: boolean
	startDate: string
}>) {
	const form = useForm()
	const xhr = useXHR()
	return (
		<FieldArray<TVisaServiceInQuote["services"][number]> name={fieldName}>
			{({ fields }) => (
				<Stack gap="6">
					{fields.map((name, index) => (
						<Stack key={name} bgColor="default" gap="4">
							<Inline gap="10" justifyContent="between" flexWrap="wrap">
								<Grid justifyContent="between">
									<Col md={6}>
										<Stack gap="4">
											<Grid noGutters gap="4" flexWrap="wrap">
												<Col>
													<SelectField
														label="Country"
														name={`${name}.country`}
														select={SelectWorldCountries}
														fullWidth
														fetchOnMount
														onChange={(
															e: TVisaServiceInQuote["services"][number]["country"]
														) => {
															form.batch(() => {
																form.change(`${name}.country`, e)
																if (e) {
																	autoSelectValues({
																		country: e,
																		name,
																		tripDestinations,
																		startDate,
																		isEditMode,
																		form,
																		xhr,
																	})
																} else {
																	form.change(`${name}.visa`, undefined)
																	form.change(`${name}.visa_type`, undefined)
																	form.change(`${name}.given_price`, undefined)
																	form.change(`${name}.pax`, totalPax)
																	form.change(`${name}.comments`, undefined)
																}
															})
														}}
													/>
												</Col>
												<Col>
													<SelectVisaService
														name={name}
														tripDestinations={tripDestinations}
														startDate={startDate}
													/>
												</Col>
												<Col>
													<SelectVisaTypeService
														name={name}
														startDate={startDate}
													/>
												</Col>
											</Grid>
											<VisaTypeCardDetails name={name} />
										</Stack>
									</Col>
									<Col md={5} paddingTop={{ xs: "4", sm: "4" }}>
										<VisaTypeMetric
											name={name}
											totalPax={totalPax}
											showBookedPrices={showBookedPrices}
											startDate={startDate}
										/>
									</Col>
								</Grid>
								<CalculateBookedPrice
									name={name}
									showBookedPrices={showBookedPrices}
								/>
							</Inline>
							{/* Remove Button */}
							<Inline justifyContent="end" marginBottom="1">
								<GetFieldValue<TVisaServiceInQuote["services"][number]>
									name={name}
								>
									{({ value: service }) => (
										<Button
											level="tertiary"
											onClick={() => fields.push(service)}
											align="center"
											size="sm"
										>
											<Icons.Duplicate /> Duplicate
										</Button>
									)}
								</GetFieldValue>
								<Button
									level="tertiary"
									onClick={() => fields.remove(index)}
									align="center"
									size="sm"
								>
									<Icons.Cancel /> Remove
								</Button>
							</Inline>
							<Divider borderTopWidth="4" marginY="0" />
						</Stack>
					))}

					{/* Add Visa Button */}
					<AddMoreVisa
						lengthCheck={(fields.length || 0) > 0}
						onClick={() =>
							fields.push({
								country: undefined,
								visa: undefined,
								visa_type: undefined,
								given_price: 0,
								per_quantity_given_price: 0,
								pax: totalPax,
								showComment: false,
							})
						}
					/>
				</Stack>
			)}
		</FieldArray>
	)
}

function AddMoreVisa({
	lengthCheck,
	onClick,
}: {
	lengthCheck: boolean
	onClick: () => void
}) {
	return (
		<FeatureFlag flag="visa">
			<Inline justifyContent={lengthCheck ? "end" : "start"}>
				{lengthCheck ? (
					<Button onClick={onClick} size="sm" level="secondary">
						<Icons.Plus /> Add More Visa
					</Button>
				) : (
					<Button onClick={onClick} level="primary">
						<Icons.Plus /> Add Visa
					</Button>
				)}
			</Inline>
		</FeatureFlag>
	)
}

function CalculateBookedPrice({
	name,
	showBookedPrices,
}: {
	name: string
	showBookedPrices?: boolean
}) {
	const form = useForm()
	const { value: pax } = useFieldValue<number>(`${name}.pax`)
	const { value: perQuantityGivenPrice } = useFieldValue<number>(
		`${name}.per_quantity_given_price`
	)
	const { value: perQuantityBookedPrice } = useFieldValue<number>(
		`${name}.per_quantity_booked_price`
	)
	useEffect(() => {
		if (showBookedPrices) {
			const calculatedPrice =
				(Number(perQuantityBookedPrice) || 0) * (Number(pax) || 0)
			const currentGivenPrice = form.getFieldState(
				`${name}.booked_price`
			)?.value
			if (currentGivenPrice !== calculatedPrice) {
				form.change(`${name}.booked_price`, calculatedPrice)
			}
		} else {
			const calculatedPrice =
				(Number(perQuantityGivenPrice) || 0) * (Number(pax) || 0)
			const currentGivenPrice = form.getFieldState(`${name}.given_price`)?.value
			if (currentGivenPrice !== calculatedPrice) {
				form.change(`${name}.given_price`, calculatedPrice)
			}
		}
	}, [
		form,
		name,
		pax,
		perQuantityGivenPrice,
		perQuantityBookedPrice,
		showBookedPrices,
	])

	return null
}

function autoSelectValues({
	country,
	name,
	tripDestinations,
	startDate,
	form,
	xhr,
}: {
	country: TVisaServiceInQuote["services"][number]["country"]
	name: string
	tripDestinations: Array<TTripDestination>
	startDate: string
	isEditMode?: boolean
	form: FormApi
	xhr: XHRInstance
}) {
	XHR(xhr)
		.getVisaServicesForSelect({
			countries: country ? [country]?.map((c) => c.iso2) : null,
			trip_destinations: tripDestinations.map((td) => td.id),
			include: "with_documents",
		})
		.then((visaServices) => {
			const firstVisa = visaServices?.[0]
			const firstVisaType =
				Array.isArray(firstVisa?.visa_types) && firstVisa.visa_types.length > 0
					? firstVisa.visa_types[0]
					: undefined
			const isExpired = isVisaRateExpired({
				rateValidityDate: firstVisaType?.price_valid_till,
				startDate,
			})
			form.batch(() => {
				form.change(`${name}.visa`, firstVisa)
				form.change(`${name}.visa_type`, firstVisaType)
				form.change(
					`${name}.per_quantity_given_price`,
					isExpired ? 0 : parseVisaRatePerPerson(firstVisaType?.selling_price)
				)
				form.change(`${name}.given_price`, undefined)
				form.change(`${name}.comments`, undefined)
			})
		})
	return null
}

function SelectVisaService({
	name,
	tripDestinations,
	startDate,
}: {
	name: string
	tripDestinations: Array<TTripDestination>
	startDate: string
}) {
	const form = useForm()
	const { value: country } = useFieldValue<
		TVisaServiceInQuote["services"][number]["country"]
	>(`${name}.country`)

	if (!country) return null
	return (
		<SelectField
			key={`visa_service_${country?.id}`}
			label="Visa"
			name={`${name}.visa`}
			fieldName={name}
			placeholder="Type to search..."
			country={country}
			select={SelectVisaServices}
			tripDestinations={tripDestinations}
			startDate={startDate}
			fetchOnMount
			onChange={(visa: TVisaServiceInQuote["services"][number]["visa"]) => {
				const firstVisaType =
					Array.isArray(visa?.visa_types) && visa.visa_types.length > 0
						? visa.visa_types[0]
						: undefined
				const isExpired = isVisaRateExpired({
					rateValidityDate: firstVisaType?.price_valid_till,
					startDate,
				})
				form.batch(() => {
					form.change(`${name}.visa`, visa)
					form.change(`${name}.visa_type`, firstVisaType)
					form.change(
						`${name}.per_quantity_given_price`,
						isExpired ? 0 : parseVisaRatePerPerson(firstVisaType?.selling_price)
					)
					form.change(`${name}.given_price`, undefined)
					form.change(`${name}.comments`, undefined)
				})
			}}
			fullWidth
		/>
	)
}

function SelectVisaTypeService({
	name,
	startDate,
}: {
	name: string
	startDate: string
}) {
	const form = useForm()
	const { value: visa } = useFieldValue<
		TVisaServiceInQuote["services"][number]["visa"]
	>(`${name}.visa`)
	const { value: visaType } = useFieldValue<
		TVisaServiceInQuote["services"][number]["visa_type"]
	>(`${name}.visa_type`)

	if (!visa) return null

	return (
		<Stack gap="2">
			<SelectField
				key={`visa_type_service_${visa?.id}`}
				label="Visa Type"
				name={`${name}.visa_type`}
				select={SelectVisaTypes}
				placeholder="Type to search..."
				fetchOnMount
				fullWidth
				visa={visa}
				onChange={(e: TVisaServiceInQuote["services"][number]["visa_type"]) => {
					const visa_type = e
					if (visa_type) {
						const isExpired = isVisaRateExpired({
							rateValidityDate: visa_type.price_valid_till,
							startDate,
						})
						form.batch(() => {
							form.change(`${name}.visa_type`, e)
							form.change(
								`${name}.per_quantity_given_price`,
								isExpired ? 0 : parseVisaRatePerPerson(e?.selling_price)
							)
						})
					} else {
						form.change(`${name}.visa_type`, e)
					}
				}}
			/>
			<VisaTypeDisabled visaType={visaType} />
		</Stack>
	)
}

function VisaTypeCommentField({
	name,
	showComment,
}: Readonly<{ name: string; showComment?: boolean }>) {
	if (!showComment) return null
	return (
		<TextInputField
			name={`${name}.comments`}
			label="Comments"
			type="text"
			size="sm"
			placeholder="Any comments regarding prices..."
		/>
	)
}

function VisaTypeCardDetails({ name }: Readonly<{ name: string }>) {
	const { value: service } = useFieldValue<
		TVisaServiceInQuote["services"][number]
	>(`${name}`)
	const { visa_type: visaType, visa } = service
	if (!visaType) return null
	const documentList = getUniqueDocumentList(
		visa?.document_groups,
		visaType.document_groups
	)
	const {
		entry_type,
		apply_before_days,
		duration_formatted,
		purpose,
		processing_time_in_hours,
	} = visaType
	const processingTime = visaProcessingTimeStr(processing_time_in_hours)
	return (
		<Stack>
			<Stack
				borderWidth="1"
				borderColor="muted"
				bgColor="subtle"
				paddingY="4"
				paddingX="6"
				rounded="md"
				gap="2"
			>
				<Inline gap="4" flexWrap="wrap">
					{duration_formatted ? (
						<InfoStack
							icon={<Icons.Calendar />}
							label="Duration"
							subtext={duration_formatted}
						/>
					) : null}
					{entry_type ? (
						<InfoStack
							icon={<Icons.Users />}
							label="Entry Type"
							subtext={getEntryTypeDisplayName(entry_type)}
						/>
					) : null}
					{purpose ? (
						<InfoStack
							icon={<Icons.Users />}
							label="Purpose of Visa"
							subtext={getPurposeOfVisaDisplayName(purpose)}
						/>
					) : null}
					{processingTime ? (
						<InfoStack
							icon={<Icons.Clock />}
							label="Processing Time"
							subtext={processingTime}
						/>
					) : null}
					{Number(apply_before_days) > 0 ? (
						<InfoStack
							icon={<Icons.CheckList />}
							label="Application Minimum Days"
							subtext={`${apply_before_days} Days`}
						/>
					) : null}
				</Inline>
				{documentList.length > 0 ? (
					<InfoStack
						icon={<Icons.Document />}
						label="Documents List"
						subtext={
							<Inline gap="2" flexWrap="wrap">
								{documentList.map((doc) => (
									<Badge key={doc.id}>
										<Text fontWeight="normal" fontSize="sm">
											{doc.name}
										</Text>
									</Badge>
								))}
							</Inline>
						}
					/>
				) : null}
			</Stack>
			{visaType.description ? (
				<Box marginY="3">
					<Component initialState={false}>
						{({ state, setState }) => (
							<Stack gap="2">
								<Box>
									<Text
										color="primary"
										fontSize="base"
										fontWeight="semibold"
										cursor="pointer"
										onClick={() => {
											setState(!state)
										}}
									>
										Description{" "}
										{state ? <Icons.ChevronUp /> : <Icons.ChevronDown />}
									</Text>
								</Box>
								{state ? <Markdown>{visaType.description}</Markdown> : null}
							</Stack>
						)}
					</Component>
				</Box>
			) : null}
		</Stack>
	)
}

function VisaTypeMetric({
	name,
	totalPax,
	showBookedPrices,
	startDate,
}: {
	name: string
	totalPax: number
	showBookedPrices?: boolean
	startDate: string
}) {
	const form = useForm()
	const xhr = useXHR()
	const { value: visaType } = useFieldValue<
		TVisaServiceInQuote["services"][number]["visa_type"]
	>(`${name}.visa_type`)
	const { value: pax } = useFieldValue<number>(`${name}.pax`)
	const { value: showComment, onChange: setShowComment } = useFieldValue<
		TVisaServiceInQuote["services"][number]["showComment"]
	>(`${name}.showComment`)
	const { value: visa } = useFieldValue<
		TVisaServiceInQuote["services"][number]["visa"]
	>(`${name}.visa`)
	const [isRefreshing, setIsRefreshing] = useState(false)

	const handleRefreshPrices = useCallback(async () => {
		if (!visaType?.id || isRefreshing) return
		setIsRefreshing(true)
		try {
			const resp = await xhr.get<{ data: typeof visaType }>(
				`/visa-types/${visaType.id}`
			)
			const updatedVisaType = resp.data.data
			const newVisaType = {
				...visaType,
				cost_price: updatedVisaType.cost_price,
				selling_price: updatedVisaType.selling_price,
			}
			form.change(`${name}.visa_type`, newVisaType)
			// In edit mode, only update the system rate display — don't touch given prices
			if (updatedVisaType.selling_price) {
				const isExpired = isVisaRateExpired({
					rateValidityDate: newVisaType?.price_valid_till,
					startDate,
				})
				const perQuantity = isExpired
					? 0
					: Number(formatMoneyByDecimal(updatedVisaType.selling_price))
				form.change(`${name}.per_quantity_given_price`, perQuantity)

				const paxNumber = Number(pax)
				if (Number.isFinite(paxNumber) && paxNumber > 0) {
					form.change(`${name}.given_price`, perQuantity * paxNumber)
				}
			}
		} catch {
			// silently fail
		} finally {
			setIsRefreshing(false)
		}
	}, [visaType, isRefreshing, xhr, form, name, pax, startDate])

	if (!visaType) return null

	return (
		<Box>
			<Stack gap="2">
				<Inline justifyContent="between" alignItems="center">
					<Text fontSize="md" fontWeight="semibold">
						Visa Prices
					</Text>
					<Inline
						gap="2"
						justifyContent="between"
						alignItems="center"
						fontSize="base"
						fontWeight="semibold"
						color="muted"
					>
						<Text
							onClick={() => {
								setShowComment(!showComment)
							}}
							cursor="pointer"
						>
							<Icons.Chat /> Comments
						</Text>
						<Text
							onClick={handleRefreshPrices}
							cursor="pointer"
							title="Refresh Prices"
						>
							<Icons.Refresh
								spin={isRefreshing}
								opacity={isRefreshing ? "50" : undefined}
							/>
						</Text>
					</Inline>
				</Inline>
				<Stack borderColor="muted" borderWidth="1" rounded="md">
					<Table
						responsive
						headers={["PAX", "Rate"].concat(
							showBookedPrices ? "Booked" : "Given"
						)}
						rows={[
							[
								<TextInputField
									type="number"
									name={`${name}.pax`}
									min={1}
									max={totalPax}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
										if (e.target.value) {
											let pax = Number(e.target.value)
											if (pax > totalPax) {
												pax = totalPax
											}
											form.change(`${name}.pax`, pax)
										} else {
											form.change(`${name}.pax`, undefined)
										}
									}}
								/>,
								<VisaTypeRatePerPerson
									visaType={visaType}
									name={name}
									startDate={startDate}
									visa={visa}
								/>,
								<VisaTypePrice
									visaType={visaType}
									name={name}
									showBookedPrices={showBookedPrices}
									visa={visa}
								/>,
							],
						]}
					/>
					<Stack gap="4" paddingX="4" paddingBottom="4">
						<VisaTypeCommentField name={name} showComment={showComment} />
						<RateValidityAlert startDate={startDate} visaType={visaType} />
					</Stack>
				</Stack>
			</Stack>
		</Box>
	)
}

function RateValidityAlert({
	visaType,
	startDate,
}: {
	startDate: string
	visaType: TVisaServiceInQuote["services"][number]["visa_type"]
}) {
	const isExpired = isVisaRateExpired({
		rateValidityDate: visaType?.price_valid_till,
		startDate,
	})
	if (!isExpired) {
		return null
	}

	return (
		<Box>
			<Alert inline hideIcon status="error">
				<Text>
					Rate validity has expired. Please update the new rate to proceed.
				</Text>
			</Alert>
		</Box>
	)
}

function VisaTypeRatePerPerson({
	visaType,
	name,
	startDate,
	visa,
}: Readonly<{
	visaType: TVisaServiceInQuote["services"][number]["visa_type"]
	name: string
	startDate: string
	visa: TVisaServiceInQuote["services"][number]["visa"]
}>) {
	const { value: pax } = useFieldValue<number>(`${name}.pax`)
	const isRateExpired = visaType
		? isVisaRateExpired({
				rateValidityDate: visaType.price_valid_till,
				startDate,
			})
		: false
	if (!visaType?.cost_price) return "N/A"
	const formattedRatePerPerson = Number(
		formatMoneyByDecimal(visaType.cost_price)
	)
	const text = (
		<Text>
			x {pax} = <Money money={multipleMoneyBy(visaType.cost_price, pax)} />
		</Text>
	)
	return (
		<Stack>
			{isRateExpired ? (
				<Box>
					<Box as="span" color="muted" fontSize="xs" verticalAlign="super">
						<InlineSelectTenantCurrencyInputField
							name={`${name}.visa.currency`}
							noCaret
							defaultValue={visa?.currency}
						/>
					</Box>{" "}
					<Text color="warning" title="Not Available" as="span">
						N/A
					</Text>
				</Box>
			) : (
				<Stack>
					<Money
						currency={visaType?.cost_price.currency}
						amount={formattedRatePerPerson}
						showCurrency
					/>
					<Text fontSize="xs" color="muted">
						{text}
					</Text>
				</Stack>
			)}
		</Stack>
	)
}

function VisaTypePrice({
	visaType,
	name,
	showBookedPrices,
	visa,
}: {
	visaType: TVisaServiceInQuote["services"][number]["visa_type"]
	name: string
	showBookedPrices?: boolean
	visa: TVisaServiceInQuote["services"][number]["visa"]
}) {
	const form = useForm()
	const [text, setText] = useState<React.ReactNode>(null)
	const { value: pax } = useFieldValue<number>(`${name}.pax`)
	const { value: givenPrice } = useFieldValue<number>(`${name}.given_price`)
	const { value: bookedPrice } = useFieldValue<number>(`${name}.booked_price`)
	useEffect(() => {
		if (Number(bookedPrice) > 0 && showBookedPrices) {
			setText(
				<Text>
					x {pax} ={" "}
					<Money currency={visa?.currency || "INR"} amount={bookedPrice} />
				</Text>
			)
		} else if (Number(givenPrice) > 0) {
			setText(
				<Text>
					x {pax} ={" "}
					<Money currency={visa?.currency || "INR"} amount={givenPrice} />
				</Text>
			)
		} else {
			setText(null)
		}
	}, [showBookedPrices, bookedPrice, givenPrice, visa, pax])

	if (!visaType?.cost_price) return "N/A"
	const perQuantityName = showBookedPrices
		? `${name}.per_quantity_booked_price`
		: `${name}.per_quantity_given_price`

	return (
		<Stack gap="2">
			<TextInputField
				type="number"
				name={perQuantityName}
				onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
					form.change(perQuantityName, e.target.value)
				}}
				min={1}
				style={{ maxWidth: "135px" }}
			/>
			<Text fontSize="sm" color="muted">
				{text}
			</Text>
		</Stack>
	)
}
